import React, { useEffect, useState, useRef, useCallback } from "react";
import "./ElegantHero.css";
import QRCodeStyling from "qr-code-styling";
import Preloader from "../components/Preloader";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { socialLinks, getSocialIcon } from "../config/socialLinks";

interface Props {
  photoBackground: string;
  inviteNom: string;
  jourSemaine: string;
  date: string;
  heure: string;
  dateISO: string;
  heureISO: string;
  invitationImage?: string;
  tableName: string;
  boissonsSansAlcool?: string[];
  boissonsAvecAlcool?: string[];
  numeroMarie: string;
  numeroPicApic: string;
  mapEmbedUrl: string;
  bride: string;
  groom: string;
  theme?: {
    background?: string;
    heart?: string;
    text?: string;
    accent?: string;
    overlay?: string;
    box?: string;
    accentLine?: string;
    inviteBoxBackground?: string;
    sectionsBackground?: string;
  };
  couple?: string;
  inviteToken?: string;
  mariageId?: string;
  livreOrActive?: boolean;
}

interface InviteInfoResponse {
  success: boolean;
  mariageId: string;
  invite?: {
    nom?: string;
    table?: string;
  };
}

interface InvitationInfoResponse {
  success: boolean;
  mariageId: string;
}

interface UpdateResponse {
  success: boolean;
  message?: string;
}

const getUploadedImagePath = (imageName: string): string => {
  if (!imageName || imageName.trim() === '') return '';
  const cleanName = imageName.trim().replace(/\\/g, '/');
  
  if (cleanName.startsWith('http://') || cleanName.startsWith('https://') || cleanName.startsWith('data:')) {
    return cleanName;
  }
  
  const baseUrl = window.location.origin;
  let fileName = cleanName;
  
  if (fileName.startsWith('uploads/')) {
    fileName = fileName.replace('uploads/', '');
  }
  if (fileName.startsWith('/uploads/')) {
    fileName = fileName.replace('/uploads/', '');
  }
  if (fileName.includes('/')) {
    const parts = fileName.split('/');
    fileName = parts[parts.length - 1];
  }
  
  return `${baseUrl}/uploads/${fileName}`;
};

const parseBoissons = (boissons: string[] | undefined): string[] => {
  if (!boissons || !Array.isArray(boissons)) return [];
  return boissons
    .filter(item => item && typeof item === 'string')
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

const extractGoogleMapsSrc = (iframeCode: string): { viewUrl: string } => {
  const srcMatch = iframeCode.match(/src="([^"]+)"/);
  let embedUrl = iframeCode;
  let viewUrl = "https://www.google.com/maps";
  if (srcMatch && srcMatch[1]) {
    embedUrl = srcMatch[1];
    const latMatch = embedUrl.match(/!3d(-?\d+\.\d+)/);
    const lngMatch = embedUrl.match(/!2d(-?\d+\.\d+)/);
    if (latMatch && lngMatch) {
      const lat = latMatch[1];
      const lng = lngMatch[1];
      viewUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
  }
  return { viewUrl };
};

const ElegantHero: React.FC<Props> = ({
  photoBackground,
  inviteNom,
  jourSemaine,
  date,
  heure,
  dateISO,
  heureISO,
  invitationImage,
  tableName,
  boissonsSansAlcool = [],
  boissonsAvecAlcool = [],
  numeroMarie,
  numeroPicApic,
  mapEmbedUrl,
  bride,
  groom,
  theme = {},
  couple = "non",
  inviteToken: propInviteToken = "",
  mariageId: propMariageId = "",
  livreOrActive = true,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [selectedBoisson, setSelectedBoisson] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [currentMariageId, setCurrentMariageId] = useState<string>(propMariageId || "");
  const [boissonNotification, setBoissonNotification] = useState<string | null>(null);
  const [countdown, setCountdown] = useState("");
  const [rsvpDeadline, setRsvpDeadline] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  
  const params = useParams();
  const urlToken = params.token as string | undefined;
  const inviteToken = urlToken || propInviteToken;
  const hasFetchedMariageId = useRef(false);
  
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const [qrInitialized, setQrInitialized] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  const colors = {
    background: theme.background || "#faf7f2",
    heart: theme.heart || "#c4a484",
    text: theme.text || "#4a4a4a",
    accent: theme.accent || "#f0ebe6",
    overlay: theme.overlay || "rgba(250, 247, 242, 0.3)",
    box: theme.box || "rgba(196, 164, 132, 0.1)",
    accentLine: theme.accentLine || "#c4a484",
    inviteBoxBackground: theme.inviteBoxBackground || "#ffffff",
    sectionsBackground: theme.sectionsBackground || "#faf7f2",
  };

  const cleanedBoissonsSansAlcool = parseBoissons(boissonsSansAlcool);
  const cleanedBoissonsAvecAlcool = parseBoissons(boissonsAvecAlcool);
  const { viewUrl: googleMapsViewUrl } = extractGoogleMapsSrc(mapEmbedUrl);

  useEffect(() => {
    const oldMeta = document.querySelectorAll('meta[property^="og:"]');
    oldMeta.forEach(meta => meta.remove());
    
    const meta = document.createElement('meta');
    meta.setAttribute('property', 'og:title');
    meta.setAttribute('content', `Mariage de ${bride} & ${groom}`);
    document.head.appendChild(meta);
    
    const meta2 = document.createElement('meta');
    meta2.setAttribute('property', 'og:description');
    meta2.setAttribute('content', 'Nous avons l\'honneur de vous inviter à célébrer notre mariage');
    document.head.appendChild(meta2);
    
    const meta3 = document.createElement('meta');
    meta3.setAttribute('property', 'og:image');
    meta3.setAttribute('content', getUploadedImagePath(invitationImage || photoBackground));
    document.head.appendChild(meta3);
    
    const meta4 = document.createElement('meta');
    meta4.setAttribute('property', 'og:url');
    meta4.setAttribute('content', window.location.href);
    document.head.appendChild(meta4);
    
    const meta5 = document.createElement('meta');
    meta5.setAttribute('property', 'og:type');
    meta5.setAttribute('content', 'website');
    document.head.appendChild(meta5);
    
    return () => {
      const metas = document.querySelectorAll('meta[property^="og:"]');
      metas.forEach(meta => meta.remove());
    };
  }, [bride, groom, invitationImage, photoBackground]);

  useEffect(() => {
    if (hasFetchedMariageId.current) return;
    
    const fetchMariageId = async () => {
      hasFetchedMariageId.current = true;
      if (propMariageId && propMariageId.trim() && propMariageId !== 'unknown') {
        setCurrentMariageId(propMariageId);
        return;
      }
      if (!inviteToken) {
        setCurrentMariageId('unknown');
        return;
      }
      try {
        const savedData = localStorage.getItem(`invite_${inviteToken}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.mariageId && parsedData.mariageId !== 'unknown') {
            setCurrentMariageId(parsedData.mariageId);
            return;
          }
        }
      } catch (error) {}
      try {
        const response = await api.get<InviteInfoResponse>(`/invites/info/${inviteToken}`);
        if (response.data.success && response.data.mariageId) {
          setCurrentMariageId(response.data.mariageId);
          localStorage.setItem(`invite_${inviteToken}`, JSON.stringify({
            mariageId: response.data.mariageId,
            token: inviteToken,
            nom: response.data.invite?.nom || inviteNom,
            table: response.data.invite?.table || tableName,
            timestamp: new Date().toISOString()
          }));
          return;
        }
        const fallbackResponse = await api.get<InvitationInfoResponse>(`/invitation-info/${inviteToken}`);
        if (fallbackResponse.data.success && fallbackResponse.data.mariageId) {
          setCurrentMariageId(fallbackResponse.data.mariageId);
          return;
        }
        setCurrentMariageId('unknown');
      } catch (error) {
        setCurrentMariageId('unknown');
      }
    };
    fetchMariageId();
  }, [propMariageId, inviteToken, inviteNom, tableName]);

  useEffect(() => {
    const eventDate = new Date(`${dateISO}T${heureISO}`);
    if (isNaN(eventDate.getTime())) {
      setCountdown("Date invalide");
      return;
    }
    const updateCountdown = () => {
      const now = new Date();
      const diff = eventDate.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown("C'est maintenant !");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setCountdown(`${days}j ${hours}h ${minutes}m ${seconds}s`);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    const deadline = new Date(eventDate);
    deadline.setDate(deadline.getDate() - 12);
    setRsvpDeadline(deadline.toLocaleDateString("fr-FR", {
      day: "numeric", month: "long", year: "numeric"
    }));
    return () => clearInterval(interval);
  }, [dateISO, heureISO]);

  useEffect(() => {
    if (inviteToken) {
      const savedData = localStorage.getItem(`invite_${inviteToken}`);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setConfirmed(parsedData.statut === 'confirmed');
          setSelectedBoisson(parsedData.boissonPreferee || null);
          if (parsedData.mariageId && parsedData.mariageId !== 'unknown') {
            setCurrentMariageId(parsedData.mariageId);
          }
        } catch (error) {}
      }
    }
  }, [inviteToken]);

  useEffect(() => {
    if (!isLoading && !qrInitialized && qrRef.current) {
      const qrData = inviteToken && inviteToken.trim() !== "" 
        ? inviteToken 
        : `INVITATION MARIAGE\n\nNom: ${inviteNom}\nTable: ${tableName}\n\n${bride} & ${groom}\n${jourSemaine} ${date} à ${heure}`;
      
      qrCode.current = new QRCodeStyling({
        width: 240,
        height: 240,
        data: qrData,
        margin: 8,
        qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
        dotsOptions: { color: colors.background, type: "rounded" },
        backgroundOptions: { color: "#ffffff" },
        cornersSquareOptions: { color: colors.heart, type: "extra-rounded" },
        cornersDotOptions: { color: colors.heart, type: "dot" },
      });
      if (qrRef.current) {
        qrRef.current.innerHTML = "";
        qrCode.current.append(qrRef.current);
      }
      setQrInitialized(true);
    }
  }, [isLoading, qrInitialized, colors.heart, colors.background, inviteToken, inviteNom, tableName, bride, groom, jourSemaine, date, heure]);

  useEffect(() => {
    if (confirmed && qrRef.current) {
      const qrData = inviteToken && inviteToken.trim() !== "" 
        ? inviteToken 
        : `INVITATION MARIAGE\n\nNom: ${inviteNom}\nTable: ${tableName}\n\n${bride} & ${groom}\n${jourSemaine} ${date} à ${heure}`;
      
      if (qrCode.current) {
        qrCode.current.update({ data: qrData });
      } else {
        qrCode.current = new QRCodeStyling({
          width: 240,
          height: 240,
          data: qrData,
          margin: 8,
          qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "H" },
          dotsOptions: { color: colors.background, type: "rounded" },
          backgroundOptions: { color: "#ffffff" },
          cornersSquareOptions: { color: colors.heart, type: "extra-rounded" },
          cornersDotOptions: { color: colors.heart, type: "dot" },
        });
        
        if (qrRef.current) {
          qrRef.current.innerHTML = "";
          qrCode.current.append(qrRef.current);
        }
      }
    }
  }, [confirmed, colors.heart, colors.background, inviteToken, inviteNom, tableName, bride, groom, jourSemaine, date, heure]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          setScrolled(window.scrollY > 100);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const saveChoicesToBackend = useCallback(async (statut: string, boissonPreferee?: string) => {
    if (!inviteToken) {
      setSaveMessage("Token d'invitation manquant.");
      return;
    }
    const localData = {
      statut,
      boissonPreferee: boissonPreferee || null,
      mariageId: currentMariageId || 'unknown',
      inviteToken,
      timestamp: new Date().toISOString(),
      inviteNom,
      bride,
      groom
    };
    localStorage.setItem(`invite_${inviteToken}`, JSON.stringify(localData));
    if (!currentMariageId || currentMariageId === 'unknown') {
      setSaveMessage("En attente de l'ID du mariage... Sauvegarde locale.");
      return;
    }
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const response = await api.put<UpdateResponse>(`/invites/update/${inviteToken}`, {
        statut,
        boissonPreferee: boissonPreferee || null
      });
      const data = response.data;
      if (data && data.success) {
        if (statut === 'pending') {
          setSaveMessage("Annulé avec succès !");
        } else {
          setSaveMessage("Confirmé avec succès !");
        }
        localStorage.removeItem(`invite_${inviteToken}`);
      } else {
        setSaveMessage("Erreur: " + (data?.message || 'Erreur inconnue'));
      }
    } catch (error: any) {
      setSaveMessage("Erreur réseau. Données sauvegardées localement.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 5000);
    }
  }, [currentMariageId, inviteToken, inviteNom, bride, groom]);

  const handleConfirmPresence = () => {
    const newConfirmedState = !confirmed;
    setConfirmed(newConfirmedState);
    if (newConfirmedState) {
      setSaveMessage("Vous avez confirmé votre présence.");
    } else {
      setSaveMessage("Vous avez annulé votre confirmation.");
    }
    const statut = newConfirmedState ? 'confirmed' : 'pending';
    saveChoicesToBackend(statut, selectedBoisson || undefined);
  };

  const handleSelectBoisson = (boisson: string) => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour choisir une boisson.");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    
    const newBoisson = selectedBoisson === boisson ? null : boisson;
    setSelectedBoisson(newBoisson);
    if (newBoisson) {
      setBoissonNotification(`Vous avez sélectionné: ${newBoisson}`);
      setTimeout(() => setBoissonNotification(null), 3000);
    }
    if (confirmed && newBoisson) {
      saveChoicesToBackend('confirmed', newBoisson);
    }
  };

  const handleDownload = () => {
    if (!confirmed) {
      setSaveMessage("⚠️ Veuillez d'abord confirmer votre présence pour télécharger votre QR code.");
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }
    qrCode.current?.download({
      name: `qr-invitation-${inviteNom.replace(/\s+/g, "-")}`,
      extension: "png",
    });
  };

  if (isLoading) {
    return <Preloader 
      bride={bride}
      groom={groom}
      colors={{
        background: colors.background,
        heart: colors.heart,
        text: colors.text,
        accent: colors.accent
      }}
      durationMs={980}
      onFinish={() => setIsLoading(false)}
    />;
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Josefin+Sans:wght@200;300;400;500&display=swap');

        .save-message {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 16px 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          z-index: 1000;
          max-width: 400px;
          font-weight: 400;
          animation: slideIn 0.3s ease-out;
          font-family: 'Josefin Sans', sans-serif;
          font-size: 14px;
          letter-spacing: 0.5px;
          border: none;
          backdrop-filter: blur(10px);
        }
        
        .boisson-notification {
          position: fixed;
          top: 80px;
          right: 20px;
          padding: 16px 24px;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          z-index: 1000;
          max-width: 400px;
          font-weight: 400;
          animation: slideIn 0.3s ease-out;
          background-color: #ffffff;
          color: #2c3e50;
          border: none;
          font-family: 'Josefin Sans', sans-serif;
          backdrop-filter: blur(10px);
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0%); opacity: 1; }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes scrollPulse {
          0%, 100% { transform: scaleX(1); opacity: 0.5; }
          50% { transform: scaleX(1.5); opacity: 1; }
        }

        @keyframes pulseGlow {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>

      {saveMessage && (
        <div className="save-message" style={{
          backgroundColor: '#ffffff',
          color: saveMessage.includes('Annulé') ? '#e74c3c' : 
                 saveMessage.includes('En attente') ? '#f39c12' : '#27ae60',
          borderLeft: `3px solid ${saveMessage.includes('Annulé') ? '#e74c3c' : 
                      saveMessage.includes('En attente') ? '#f39c12' : '#27ae60'}`
        }}>
          {saveMessage}
        </div>
      )}

      {boissonNotification && (
        <div className="boisson-notification">
          {boissonNotification}
        </div>
      )}

      <div className="elegant-wrapper" ref={heroRef}>
        
        <div 
          className="parallax-container" 
          ref={parallaxRef}
          style={{
            clipPath: 'polygon(0 0, 100% 0, 100% 95%, 0 100%)',
          }}
        >
          
          <div 
            className="elegant-parallax-bg"
            style={{ 
              backgroundImage: `url(${getUploadedImagePath(photoBackground)})`,
              transform: `scale(${1 + scrollY * 0.0005})`,
            }}
          >
            <div className="elegant-overlay" style={{ background: colors.overlay }}></div>
          </div>

          <section className="elegant-section hero-section">
            <div className="elegant-hero-content">
              <div className="elegant-hero-inner">
                <h1 className="elegant-hero-title" style={{ color: colors.text }}>
                  {bride} <span style={{ color: colors.heart }}>&</span> {groom}
                </h1>
                <p className="elegant-hero-guest" style={{ color: colors.text }}>{inviteNom}</p>
                <div className="elegant-hero-date" style={{ color: colors.heart }}>
                  {jourSemaine} <span style={{ color: colors.text }}>{date}</span> • {heure}
                </div>
              </div>
            </div>
            
            <div className={`elegant-scroll ${scrolled ? 'hidden' : ''}`}>
              <div className="elegant-scroll-line" style={{ backgroundColor: colors.heart }}></div>
            </div>
          </section>

          <section className="elegant-section invite-section">
            <div className="elegant-container">
              
              <img 
                src="/assets/elegant/calligraphy-invitation.png" 
                alt="Invitation" 
                className="elegant-asset-invitation-xlarge"
              />
              
              <div className="elegant-glass-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30'
              }}>
                
                <p className="elegant-invite-message-large" style={{ color: colors.text }}>
                  Entourés de ceux que nous aimons, nous célébrerons bientôt notre union.<br />
                  Votre présence serait pour nous un immense bonheur.
                </p>
                
                <div className="elegant-date-frame-subtle" style={{ 
                  borderColor: colors.accentLine + '40',
                  background: `${colors.heart}05`
                }}>
                  <div className="elegant-date-line-large" style={{ color: colors.text }}>
                    {jourSemaine} {date.split(' ')[0]}
                  </div>
                  <div className="elegant-date-line-large" style={{ color: colors.text }}>
                    {date.split(' ')[1]} {date.split(' ')[2]}
                  </div>
                  <div className="elegant-date-line-large" style={{ color: colors.text }}>
                    {heure}
                  </div>
                </div>
                
              </div>
              
              <div className="elegant-lines-outside">
                <div className="elegant-line-long-outside" style={{ backgroundColor: colors.heart }}></div>
                <div className="elegant-line-short-outside" style={{ backgroundColor: colors.heart }}></div>
              </div>
              
              <div style={{ height: '150px' }}></div>
              
              <div className="elegant-countdown-box" style={{ 
                borderColor: colors.accentLine + '30',
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                marginBottom: '100px'
              }}>
                <span className="elegant-countdown-value-large" style={{ color: colors.heart }}>{countdown}</span>
              </div>
              
              <div style={{ height: '150px' }}></div>
              
            </div>
          </section>
          
        </div>

        <div className="parallax-end-line">
          <div className="parallax-end-container">
            <div className="parallax-end-marker" style={{ backgroundColor: colors.heart }}></div>
            <span className="parallax-end-text" style={{ color: colors.text }}>
              Fin du voyage dans le paysage
            </span>
            <div className="parallax-end-marker" style={{ backgroundColor: colors.heart }}></div>
          </div>
        </div>

        <div className="normal-section" style={{ backgroundColor: colors.background }}>
          
          {/* Image d'invitation - CORRIGÉE */}
          {invitationImage && (
            <section className="elegant-section image-section" style={{ 
              padding: 0,
              margin: 0,
            }}>
              <img 
                src={getUploadedImagePath(invitationImage)} 
                alt="Invitation"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  display: 'block',
                  maxWidth: '100%'
                }}
              />
            </section>
          )}
          
          <div className="section-separator">
            <div className="separator-line1" style={{ backgroundColor: colors.text }}></div>
          </div>
          
          <section className="elegant-section rsvp-section">
            <div className="elegant-container">
              
              <img 
                src="/assets/elegant/confirmation.png" 
                alt="Confirmation" 
                className="elegant-asset-confirmation"
              />
              
              <div className="elegant-glass-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30'
              }}>
                
                <p className="elegant-rsvp-message" style={{ color: colors.text }}>
                  Nous vous remercions de bien vouloir confirmer votre présence
                </p>
                
                <p className="elegant-rsvp-deadline" style={{ color: colors.text }}>
                  avant le <span style={{ color: colors.heart }}>{rsvpDeadline}</span>
                </p>
                
                <div className="elegant-button-frame" style={{ borderColor: colors.heart }}>
                  <button 
                    className="elegant-rsvp-button"
                    style={{ 
                      backgroundColor: confirmed ? 'transparent' : colors.heart,
                      color: confirmed ? colors.heart : '#ffffff',
                    }}
                    onClick={handleConfirmPresence}
                    disabled={isSaving}
                  >
                    {isSaving ? '...' : (confirmed ? 'présence confirmée' : 'confirmer ma présence')}
                  </button>
                </div>
                
                {confirmed && (
                  <p className="elegant-confirmed-message" style={{ color: colors.heart }}>
                    Merci ! Nous avons hâte de vous retrouver.
                  </p>
                )}
                
              </div>
              
            </div>
          </section>
          
          <div className="section-separator">
            <div className="separator-line" style={{ backgroundColor: colors.text }}></div>
          </div>

          <section className="elegant-section qr-section">
            <div className="elegant-container">
              
              <img 
                src="/assets/elegant/calligraphy-qrcode.png" 
                alt="QR Code" 
                className="elegant-asset-qr"
              />
              
              <div className="elegant-glass-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30',
                padding: '50px 40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                
                <p className="elegant-qr-text" style={{ color: colors.text }}>
                  Voici votre pass d'entrée à présenter le jour J.
                </p>
                
                {!confirmed ? (
                  <div className="elegant-warning-message" style={{ 
                    textAlign: 'center', 
                    padding: '30px',
                    backgroundColor: `${colors.heart}15`,
                    borderRadius: '16px',
                    margin: '20px 0',
                    width: '100%'
                  }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heart}/>
                    </svg>
                    <p style={{ color: colors.text, fontSize: '16px', marginBottom: '15px' }}>
                      Veuillez d'abord confirmer votre présence pour accéder à votre QR code.
                    </p>
                    <button 
                      onClick={() => document.querySelector('.elegant-rsvp-button')?.scrollIntoView({ behavior: 'smooth' })}
                      style={{
                        background: colors.heart,
                        color: '#fff',
                        border: 'none',
                        padding: '10px 25px',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontFamily: 'Josefin Sans, sans-serif'
                      }}
                    >
                      Confirmer ma présence
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="elegant-qr-frame" style={{ borderColor: colors.accentLine + '40' }}>
                      <div className="elegant-qr-wrapper" ref={qrRef}></div>
                    </div>
                    
                    <div className="elegant-button-frame" style={{ borderColor: colors.heart, marginTop: '20px' }}>
                      <button 
                        className="elegant-qr-download"
                        style={{ 
                          backgroundColor: 'transparent',
                          color: colors.heart
                        }}
                        onClick={handleDownload}
                      >
                        télécharger le QR code
                      </button>
                    </div>
                  </>
                )}
                
              </div>
              
            </div>
          </section>
          
          <div className="section-separator">
            <div className="separator-line" style={{ backgroundColor: colors.text }}></div>
          </div>

          {(cleanedBoissonsSansAlcool.length > 0 || cleanedBoissonsAvecAlcool.length > 0) && (
            <section className="elegant-section boissons-section">
              <div className="elegant-container">
                
                <img 
                  src="/assets/elegant/calligraphy-drinks.png" 
                  alt="Boissons" 
                  className="elegant-asset-drinks"
                />
                
                <img 
                  src="/assets/elegant/champagne-glass.png" 
                  alt="" 
                  className="elegant-glass-icon"
                />
                
                <div className="elegant-glass-box" style={{ 
                  background: `${colors.heart}10`,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: colors.accentLine + '30'
                }}>
                  
                  <p className="elegant-boissons-intro" style={{ color: colors.text }}>
                    Pour mieux vous recevoir, dites-nous votre préférence
                  </p>
                  
                  {!confirmed ? (
                    <div className="elegant-warning-message" style={{ 
                      textAlign: 'center', 
                      padding: '30px',
                      backgroundColor: `${colors.heart}15`,
                      borderRadius: '16px',
                      margin: '20px 0'
                    }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '15px' }}>
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill={colors.heart}/>
                      </svg>
                      <p style={{ color: colors.text, fontSize: '16px', marginBottom: '15px' }}>
                        Veuillez d'abord confirmer votre présence pour choisir votre boisson.
                      </p>
                      <button 
                        onClick={() => document.querySelector('.elegant-rsvp-button')?.scrollIntoView({ behavior: 'smooth' })}
                        style={{
                          background: colors.heart,
                          color: '#fff',
                          border: 'none',
                          padding: '10px 25px',
                          borderRadius: '50px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontFamily: 'Josefin Sans, sans-serif'
                        }}
                      >
                        Confirmer ma présence
                      </button>
                    </div>
                  ) : (
                    <>
                      {cleanedBoissonsSansAlcool.length > 0 && (
                        <div className="elegant-boissons-category">
                          <h3 style={{ color: colors.heart }}>sans alcool</h3>
                          <div className="elegant-boissons-grid">
                            {cleanedBoissonsSansAlcool.map((item, index) => (
                              <button
                                key={index}
                                className={`elegant-boisson-item ${selectedBoisson === item ? 'selected' : ''}`}
                                style={{ 
                                  backgroundColor: selectedBoisson === item ? colors.heart : 'transparent',
                                  color: selectedBoisson === item ? '#ffffff' : colors.text,
                                  borderColor: colors.heart
                                }}
                                onClick={() => handleSelectBoisson(item)}
                              >
                                {item}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {cleanedBoissonsAvecAlcool.length > 0 && (
                        <div className="elegant-boissons-category">
                          <h3 style={{ color: colors.heart }}>avec alcool</h3>
                          <div className="elegant-boissons-grid">
                            {cleanedBoissonsAvecAlcool.map((item, index) => (
                              <button
                                key={index}
                                className={`elegant-boisson-item ${selectedBoisson === item ? 'selected' : ''}`}
                                style={{ 
                                  backgroundColor: selectedBoisson === item ? colors.heart : 'transparent',
                                  color: selectedBoisson === item ? '#ffffff' : colors.text,
                                  borderColor: colors.heart
                                }}
                                onClick={() => handleSelectBoisson(item)}
                              >
                                {item}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {selectedBoisson && (
                        <p className="elegant-boisson-selected" style={{ color: colors.heart }}>
                          {selectedBoisson}
                        </p>
                      )}
                    </>
                  )}
                  
                </div>
                
              </div>
            </section>
            
          )}
        

          {livreOrActive && (
            <section className="elegant-section livre-section">
              <div className="elegant-container">
                
                <img 
                  src="/assets/elegant/calligraphy-guestbook.png" 
                  alt="Livre d'or" 
                  className="elegant-asset-livre"
                />
                
                <img 
                  src="/assets/elegant/elegant-book.png" 
                  alt="" 
                  className="elegant-book-icon"
                />
                
                <div className="elegant-glass-box" style={{ 
                  background: `${colors.heart}10`,
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: colors.accentLine + '30'
                }}>
                  
                  <p className="elegant-livre-text" style={{ color: colors.text }}>
                    Laissez un petit mot dans notre livre d'or.<br />
                    Vos pensées et vos vœux resteront un précieux souvenir pour nous.
                  </p>
                  
                  <form 
                    className="elegant-livre-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const message = (e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement).value;
                      
                      const messageComplet = `LIVRE D'OR

De la part de : ${inviteNom}
Message : "${message}"

Merci infiniment pour votre message. Nous vous sommes très reconnaissants.`;
                      
                      const url = `https://wa.me/${numeroMarie}?text=${encodeURIComponent(messageComplet)}`;
                      window.open(url, "_blank");
                      
                      alert("✅ Message envoyé avec succès !");
                      (e.target as HTMLFormElement).reset();
                    }}
                  >
                    <textarea 
                      name="message" 
                      placeholder="Votre message..."
                      className="elegant-livre-input"
                      style={{ 
                        borderColor: colors.heart + '40',
                        color: colors.text,
                        backgroundColor: 'rgba(255,255,255,0.05)'
                      }}
                      required
                    />
                    
                    <div className="elegant-button-frame" style={{ borderColor: colors.heart }}>
                      <button 
                        type="submit" 
                        className="elegant-livre-submit"
                        style={{ 
                          backgroundColor: 'transparent',
                          color: colors.heart
                        }}
                      >
                        envoyer
                      </button>
                    </div>
                  </form>
                  
                </div>
                
              </div>
            </section>
          )}
          
          <div className="section-separator" style={{ marginBottom: 0, paddingBottom: 0 }}>
            <div className="separator-line" style={{ backgroundColor: colors.text }}></div>
          </div>

          <section className="elegant-section rappel-section" style={{ 
            padding: 0,
            marginTop: 0
          }}>
            
            <div 
              className="elegant-window-full"
              style={{ 
                backgroundImage: `url(${getUploadedImagePath(photoBackground)})`,
                transform: `translate3d(0, ${scrollY * 0.02}px, 0)`,
              }}
            >
              <div className="elegant-window-overlay" style={{ background: colors.overlay }}></div>
            </div>
            
          </section>
          
          <div className="section-separator">
            <div className="separator-line1" style={{ backgroundColor: colors.text }}></div>
          </div>

          <section className="elegant-section map-section">
            <div className="elegant-container">
              
              <img 
                src="/assets/elegant/location-icon.png" 
                alt="Lieu" 
                className="elegant-asset-map"
              />
              
              <div className="elegant-glass-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30',
                padding: '30px 30px 40px'
              }}>
                
                <a href={googleMapsViewUrl} target="_blank" rel="noopener noreferrer" className="elegant-map-link">
                  <img 
                    src="/assets/elegant/carte.png" 
                    alt="Carte du lieu"
                    className="elegant-map-image"
                  />
                </a>
                
                <div className="elegant-button-frame" style={{ borderColor: colors.heart, marginTop: '25px' }}>
                  <a 
                    href={googleMapsViewUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="elegant-map-button"
                    style={{ color: colors.heart }}
                  >
                    itinéraire
                  </a>
                </div>
                
              </div>
              
            </div>
          </section>
          
          <div className="section-separator">
            <div className="separator-line" style={{ backgroundColor: colors.text }}></div>
          </div>

          <section className="elegant-section contact-section">
            <div className="elegant-container">
              
              <img 
                src="/assets/elegant/calligraphy-contact.png" 
                alt="Contact" 
                className="elegant-asset-contact"
              />
              
              <img 
                src="/assets/elegant/whatsapp-gold.png" 
                alt="WhatsApp" 
                className="elegant-whatsapp-icon"
              />
              
              <div className="elegant-glass-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30'
              }}>
                
                <p className="elegant-contact-text" style={{ color: colors.text }}>
                  Une question ou un doute ?<br />
                  Nous restons disponibles pour vous répondre.
                </p>
                
                <div className="elegant-button-frame" style={{ borderColor: colors.heart }}>
                  <a 
                    href={`https://wa.me/${numeroPicApic}?text=${encodeURIComponent("Bonjour PicApic, j'aimerais obtenir plus d'informations concernant le mariage.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="elegant-contact-button"
                    style={{ 
                      backgroundColor: 'transparent',
                      color: colors.heart
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                      <path d="M19.05 4.91C17.18 3.24 14.81 2.2 12.2 2.04 5.95 1.56 2.2 6.38 2.2 9.4c0 1.4.5 2.7 1.3 3.8L3 18.9l5.8-1.6c1.2.7 2.5 1.1 3.9 1.1 5.3 0 9.1-4.8 9.1-9.1 0-2.4-1-4.7-2.8-6.4z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
                
              </div>
              
            </div>
          </section>

          <div className="section-separator">
            <div className="separator-line" style={{ backgroundColor: colors.text }}></div>
          </div>

          <footer className="elegant-footer" style={{ backgroundColor: colors.background }}>
            <div className="elegant-container">
              
              <div className="elegant-footer-box" style={{ 
                background: `${colors.heart}10`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderColor: colors.accentLine + '30'
              }}>
                
                <div className="elegant-footer-frame">
                  <div className="elegant-corner tl" style={{ borderColor: colors.text }}></div>
                  <div className="elegant-corner tr" style={{ borderColor: colors.text }}></div>
                  <div className="elegant-corner bl" style={{ borderColor: colors.text }}></div>
                  <div className="elegant-corner br" style={{ borderColor: colors.text }}></div>
                  
                  <div className="elegant-footer-content">
                    <p className="elegant-footer-couple" style={{ color: colors.text }}>
                      {bride} & {groom}
                    </p>
                    <p className="elegant-footer-date" style={{ color: colors.heart }}>
                      {jourSemaine} {date}
                    </p>
                    <div className="elegant-footer-bottom">
                      <p className="elegant-footer-copyright" style={{ color: colors.text }}>
                        © {new Date(dateISO).getFullYear()} — 
                        <a href={socialLinks.picapic} target="_blank" rel="noopener noreferrer" style={{ color: colors.heart, textDecoration: 'none' }}>
                        By  PicApic
                        </a>
                      </p>
                      <div className="elegant-social-links">
                        <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="elegant-social-link">
                          {getSocialIcon('instagram', colors.heart, 24)}
                        </a>
                        
                        <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="elegant-social-link">
                          {getSocialIcon('tiktok', colors.heart, 24)}
                        </a>
                        
                        <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="elegant-social-link">
                          {getSocialIcon('facebook', colors.heart, 24)}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </footer>
          
        </div>
        
      </div>
    </>
  );
};

export default ElegantHero;